import React, { useState, useEffect, useRef } from "react";
import PageHeader from "../../components/global-layout/page-header";
import { DashboardPageHeaderIcon, RefreshIcon } from "../../components/svg";
import IconTextButton from "../../components/form/icon-text-btn";
import Skeleton from "react-loading-skeleton";
import SelectDuration from "../../components/UI/select-duration/select-duration";
import { dashboard } from "../../services/InspectionOneServices";
import { useMyContext } from "../../components/my-context/MyContext";
import { useTranslation } from "react-i18next";
import ScrollableSplitCardBlock from "./ScrollableSplitCardBlock";
import MultipleSplitRadialBars from "./MultipleSplitRadialBars";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SelectInputWithoutForm } from "../../components/form/select-input";
import { getTodayUtcRange } from "../../utils/common";
import Authentication from "../../auth/Authentication";

interface Option {
  id: number;
  value: string;
  label: string;
}

const ReactDashboardUI: React.FC = () => {
  const authService = Authentication();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [expandedSite, setExpandedSite] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const cardContainerRef = useRef<HTMLDivElement | null>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [splitDashboard, setSplitDashboard] = useState<any[]>([]);
  const loopTimeoutRef = useRef<number | null>(null);
  const currentRequestIdRef = useRef<number>(0);
  const miniCalledRef = useRef(false);
  const { pageTitle } = useMyContext();
  const { t } = useTranslation();
  const dashboardContainer = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState<any>({ width: 0, height: 0 });
  const dashboardViews = ["chart", "card"];
  const totalDashboardViews = dashboardViews.length;
  const [currentIndex, setCurrentIndex] = useState(1);
  const [accuracyData, setAccuracyData] = useState<any[]>([])
  const shift = useRef(0)
  const [isFirstRender, setIsFirstRender]  = useState(true);
  const [lineOptions] = useState<any[]>(() => {
    try {
      const raw = authService.decryptData("masterDetails", 1);
      return raw ? (raw.processLogic ?? []) : [];
    } catch { return []; }
  });
  const processLogicId = useRef<string>(lineOptions[0]?.uniqueId ?? '');
  const [selectedLine, setSelectedLine] = useState<any>(lineOptions[0] ?? null);

  const durationOptions: Option[] = [
    { id: 1, value: "noFilter", label: "All time" },
    { id: 2, value: "today", label: "Today" },
    { id: 3, value: "thisWeek", label: "This Week" },
    { id: 4, value: "thisMonth", label: "This Month" },
    { id: 5, value: "thisYear", label: "This Year" },
    { id: 6, value: "custom", label: "Custom" },
  ];
  const [dynmaicDurationOption, setDynmaicDurationoption] = useState<Option[]>(durationOptions);
  const shiftOptions = [
    { id: 0, label:"All"},
    { id: 1, label: "Shift I"},
    { id: 2, label: "Shift II"},
    { id: 3, label: "Shift III"},
  ];
  const [selectedOption, setSelectedOption] = useState<Option>(
    durationOptions[1]
  );
  

const getDateRangeUtcForSixToSix = (
    dateFilterId: number,
    startDate?: any,
    endDate?: any
  ): { fromDateUtc: string; toDateUtc: string } => {
    const localNow = new Date();
    let fromDateUtc = "";
    let toDateUtc = "";

    switch (dateFilterId) {
      case 1:
        fromDateUtc = "";
        toDateUtc = "";
        break;

      case 2: // Today (06:00 → next day 06:00)
        {
          const { startDate, endDate } = getTodayUtcRange();
          fromDateUtc = startDate;
          toDateUtc = endDate;
        }
        break;

      case 3: // This week (Mon 06:00 → next Mon 06:00)
        {
          const day = localNow.getDay();
          const diff = day === 0 ? 6 : day - 1; // Monday as start
          const weekStartLocal = new Date(localNow);
          weekStartLocal.setDate(localNow.getDate() - diff);
          weekStartLocal.setHours(6, 0, 0, 0);

          const weekEndLocal = new Date(weekStartLocal);
          weekEndLocal.setDate(weekEndLocal.getDate() + 7);


          fromDateUtc = weekStartLocal.toISOString();
          toDateUtc = weekEndLocal.toISOString();
        }
        break;




      case 4: // This month (1st day 06:00 → 1st of next month 06:00)
        {
          const monthStartLocal = new Date(
            localNow.getFullYear(),
            localNow.getMonth(),
            1,
            6,
            0,
            0,
            0
          );
          const monthEndLocal = new Date(monthStartLocal);
          monthEndLocal.setMonth(monthEndLocal.getMonth() + 1);

          fromDateUtc = monthStartLocal.toISOString();
          toDateUtc = monthEndLocal.toISOString();
        }
        break;

      case 5: // This year (Jan 1 06:00 → Jan 1 next year 06:00)
        {
          const yearStartLocal = new Date(
            localNow.getFullYear(),
            0,
            1,
            6,
            0,
            0,
            0
          );
          const yearEndLocal = new Date(yearStartLocal);
          yearEndLocal.setFullYear(yearEndLocal.getFullYear() + 1);

          fromDateUtc = yearStartLocal.toISOString();
          toDateUtc = yearEndLocal.toISOString();
        }
        break;

      case 6: // Custom (always 06:00 → 06:00)
        {
          const startLocal = new Date(`${startDate}T06:00:00`);
          const endLocal = new Date(`${endDate}T06:00:00`);


          fromDateUtc = startLocal.toISOString();
          toDateUtc = endLocal.toISOString();
        }
        break;

      default:
        {
          const nextWeekEnd = new Date(localNow);
          const daysUntilEndOfNextWeek = ((7 - localNow.getDay()) % 7) + 7;
          nextWeekEnd.setDate(localNow.getDate() + daysUntilEndOfNextWeek + 1);
          nextWeekEnd.setHours(6, 0, 0, 0);

          fromDateUtc = new Date("1976-01-01T06:00:00").toISOString();
          toDateUtc = nextWeekEnd.toISOString();
        }
        break;
    }

    return { fromDateUtc, toDateUtc };
  };


  const stopDashboardLoop = () => {
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }
    // increment to invalidate any in-flight responses
    currentRequestIdRef.current += 1;
  };

  // Handle Option Change for fetch data
  const handleOptionChange = (option: Option | null) => {
    if (!option) return;
    stopDashboardLoop(); 
    // update selected option logic (keep your existing behavior)
    if (option.id === 6 && startDate && endDate) {
      const formattedLabel = `${startDate} - ${endDate}`;
      const customOption = { ...option, label: formattedLabel };
      setSelectedOption(customOption);
    } else {
      setSelectedOption(option);
    }

    if (option.id === 6) {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const startDashboardLoop = (option: any) => {
    // kick off immediately
    const requestId = ++currentRequestIdRef.current;
    dahboardAPICall(option, requestId);
  };

  const dahboardAPICall = async (option: any, requestId: number) => {
    // If custom date option and no dates selected, don't call
    if (option.id === 6 && (!startDate || !endDate)) {
      console.log("Waiting for date selection...");
      return;
    }
    try {
      const { fromDateUtc, toDateUtc } = getDateRangeUtcForSixToSix(
        option.id,
        startDate,
        endDate,
      );
      const requestData = {
        dateFilterId: option.id,
        startDate: fromDateUtc,
        endDate: toDateUtc,
        shift: shift.current,
        processLogicId: processLogicId.current,
      };

      const response = await dashboard.DashboardWebSplit(requestData);

      // Discard stale responses — a newer request has already started
      if (requestId !== currentRequestIdRef.current) return;

      if (response.success) {
        const dashboards = response.data.dashboards[0];
        const subProcessCardData = dashboards.subProcessCardsData;
        if (subProcessCardData?.length > 0) {
          const colorMap: Record<
            string,
            { backgroundColor: string; fontColor: string }
          > = {
            "1fe6706d-9a24-4d83-bba7-0d693874827c": {
              backgroundColor: "#E6F0FF",
              fontColor: "#0078D4",
            },
            "e9bef77f-088b-4451-b375-2e30ba942741": {
              backgroundColor: "#FFE3E3",
              fontColor: "#f44336",
            },
            "5fc746b4-ae5a-4da8-895a-9e975985388e": {
              backgroundColor: "#FFF9E6",
              fontColor: "#FFC107",
            },
            "d2d0506d-c5c1-44f5-b64c-0a3a7eddddcd": {
              backgroundColor: "#E8F5E9",
              fontColor: "#2E7D32",
            },
            "b9da840a-b3ad-4260-afde-b44cd7b1615c": {
              backgroundColor: "#F3E5F5",
              fontColor: "#6A1B9A",
            },
            "fe7c48ed-9146-4424-b715-11a0138778d6": {
              backgroundColor: "#FFFDE7",
              fontColor: "#FF8F00",
            },
            "c62dca2f-387d-4775-ae05-e6804360912z": {
              backgroundColor: "#E0F7FA",
              fontColor: "#00838F",
            },
            "1c48acd2-378a-4688-b78c-4a2af5d71ca5": {
              backgroundColor: "#FFEBEE",
              fontColor: "#C2185B",
            },
          };

          const fallbackColors = [
            { backgroundColor: "#E6F0FF", fontColor: "#0078D4" },
            { backgroundColor: "#FFE3E3", fontColor: "#f44336" },
            { backgroundColor: "#FFF9E6", fontColor: "#FFC107" },
            { backgroundColor: "#E8F5E9", fontColor: "#2E7D32" },
            { backgroundColor: "#F3E5F5", fontColor: "#6A1B9A" },
            { backgroundColor: "#FFFDE7", fontColor: "#FF8F00" },
            { backgroundColor: "#E0F7FA", fontColor: "#00838F" },
            { backgroundColor: "#FFEBEE", fontColor: "#C2185B" },
            { backgroundColor: "#FFF3E0", fontColor: "#E65100" },
            { backgroundColor: "#E3F2FD", fontColor: "#1565C0" },
            { backgroundColor: "#F9FBE7", fontColor: "#827717" },
            { backgroundColor: "#FCE4EC", fontColor: "#AD1457" },
          ];

          const formattedData = subProcessCardData.map((sub: any) => {
            const totalValue = sub.labels?.reduce(
              (acc: number, label: any) => acc + (label.value || 0),
              0
            );

            const totalObj = {
              uniqueId: "total",
              status: "Total",
              value: totalValue,
              backgroundColor: "#d9d2e9",
              fontColor: "#000000",
              imageUrl: "",
            };

            const otherLabels =
              sub.labels
                // ?.filter((label: any) => label.value !== 0)
                .map((label: any, index: number) => {
                  const colors = colorMap[label.uniqueId] ||
                    fallbackColors[index % fallbackColors.length];

                  return {
                    ...label,
                    ...colors,
                  };
                }) || [];

            return {
              title: sub.subProcessName,
              SubprocessId: sub.subProcessUniqueId,
              cardsData: sub.statuses,
              labels: [totalObj, ...otherLabels],
            };
          });

          setSplitDashboard(formattedData);
        }

        // Call mini only on first success of the current switch; setInterval handles hourly refresh
        if (!miniCalledRef.current) {
          miniCalledRef.current = true;
          handleDashboardSplitMini(option);
        }

        // Clear any lingering timeout, then start the interval for this request only
        if (loopTimeoutRef.current) {
          clearTimeout(loopTimeoutRef.current);
          loopTimeoutRef.current = null;
        }
        loopTimeoutRef.current = window.setTimeout(() => {
          const nextRequestId = ++currentRequestIdRef.current;
          dahboardAPICall(option, nextRequestId);
        }, 10000);

      } else {
        console.error("Error fetching data:", response.message);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      // Only update loading state for the request that is still current
      if (requestId === currentRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      stopDashboardLoop();
    };
  }, []);

  useEffect(() => {
    return () => {
      if ((window as any).dashboardInterval) {
        clearInterval((window as any).dashboardInterval);
      }
    };
  }, []);

  const handleClearAndFetch = () => {
    handleOptionChange(durationOptions[1]);
  };

 
useEffect(() => {
    if (!selectedOption || (selectedOption.id === 6 && (!startDate || !endDate))) {
        return;
    }
    if (isFirstRender) {
        setIsLoading(true);
    }
    miniCalledRef.current = false;
    stopDashboardLoop();
    startDashboardLoop(selectedOption);
    if (isFirstRender) {
        setIsFirstRender(false);
    }
}, [selectedOption, endDate, shift.current]); 

  useEffect(() => {
    const updateSizes = () => {
      if (dashboardContainer.current && splitDashboard.length > 0) {
        const container = dashboardContainer.current.getBoundingClientRect();

        const minCardWidth = 100;
        const maxCardWidth = 400;
        const minCardHeight = 200;
        const maxCardHeight = 200;

        const cols = Math.max(1, Math.floor(container.width / minCardWidth));
        const rows = Math.ceil(splitDashboard.length / cols);

        let cardWidth = container.width / cols;
        let cardHeight = container.height / rows;

        cardWidth = Math.min(Math.max(cardWidth, minCardWidth), maxCardWidth);
        cardHeight = Math.min(
          Math.max(cardHeight, minCardHeight),
          maxCardHeight
        );

        setCardSize({
          width: Math.floor(cardWidth),
          height: Math.floor(cardHeight),
        });
      }
    };

    updateSizes();
    window.addEventListener("resize", updateSizes);
    return () => window.removeEventListener("resize", updateSizes);
  }, [splitDashboard.length]);

  const handleRefresh = () => {
    miniCalledRef.current = false;
    setIsLoading(true);
    stopDashboardLoop();
    startDashboardLoop(selectedOption);
  };

  const checkScrollPosition = () => {
    if (cardContainerRef.current) {
      const container = cardContainerRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };
  const scrollRight = () => {
    if (cardContainerRef.current) {
      cardContainerRef.current.scrollBy({ left: 180, behavior: "smooth" });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const scrollLeft = () => {
    if (cardContainerRef.current) {
      cardContainerRef.current.scrollBy({ left: -180, behavior: "smooth" });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const handleDashboardSplitMini = async (option: any) => {
    try {
      const { fromDateUtc, toDateUtc } = getDateRangeUtcForSixToSix(
        option.id,
        startDate,
        endDate
      );
      const requestData = {
        dateFilterId: option.id,
        startDate: fromDateUtc,
        endDate: toDateUtc,
        shift: shift.current,
        processLogicId: processLogicId.current,
      };
      const resposne = await dashboard.DashboardWebSlitMini(requestData);
      if (resposne.success) {
        setAccuracyData(resposne.data)
      }
    }
    catch (error) {

    }
  }
  useEffect(() => {
    const intervalId = setInterval(() => {
      handleDashboardSplitMini(selectedOption);
    }, 3600000);

    return () => clearInterval(intervalId);
  }, [selectedOption]);

  const handlePrevPage = () => {
    setCurrentIndex((prev: number) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentIndex((prev: number) => Math.min(totalDashboardViews, prev + 1));
  };
const handleInputChange = (_key: any, option: any) => {
  if (!option) return;

  shift.current = option.id;
  if (option.id === 0) {
    setDynmaicDurationoption(durationOptions);
  } else {
    const today = { id: 2, value: "today", label: "Today" };
    const custom = { id: 6, value: "custom", label: "Custom" };
    setDynmaicDurationoption([today, custom]);
  }
};

const handleLineChange = (option: any) => {
  if (!option) return;
  processLogicId.current = option.uniqueId;
  setSelectedLine(option);
  miniCalledRef.current = false;
  stopDashboardLoop(); // clears previous interval and invalidates in-flight requests
  startDashboardLoop(selectedOption); // starts fresh; interval only opens after success
};

  return (
    <>
      {isLoading ? (
        <>
          {/* Header Skeletons */}
          <Skeleton width="15%" height={47} borderRadius={3} className="mb-4" />
          <div className="flex justify-end items-center space-x-4 mb-6">
            {/* duration input */}
            <Skeleton width="25rem" height={47} borderRadius={3} />
          </div>
          <div className="w-full h-full">
            <Skeleton width="100%" height="80%" borderRadius={3} />
          </div>
        </>
      ) : (
        <>
          <PageHeader title={t(pageTitle)} icon={<DashboardPageHeaderIcon />} />
          <div className="w-full flex">
            
            {/* //alert section */}
            <div className="w-1/2 flex gap-10 items-center">
              {/* {Array.from({ length: 2 }).map((_, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full self-center bg-io-yellow"></div>
                  <span className="break-words text-[2rem] font-bold leading-tight text-black">
                    Alert {idx + 1}
                  </span>
                </div>
              ))} */}
            </div>
            <div className="w-1/2 flex justify-end items-center gap-6">
              {lineOptions.length > 0 && (
                <div className="w-[25rem]">
                  <SelectInputWithoutForm
                    key={lineOptions[0]?.uniqueId ?? 'line-empty'}
                    labelName="Line"
                    id="line"
                    isRequired={false}
                    options={lineOptions}
                    getOptionLabel={(opt: any) => opt.name}
                    getOptionValue={(opt: any) => opt.uniqueId}
                    defaultValue={selectedLine}
                    getSelection={handleLineChange}
                    isSearchable={false}
                  />
                </div>
              )}
              <div className="w-[25rem]">
                <SelectInputWithoutForm
                    labelName="shift"
                    id="shift"
                    isRequired={false}
                    options={shiftOptions}
                    getOptionLabel={(option: any) => option.label}
                    getOptionValue={(option: any) => option.id}
                    defaultValue={shiftOptions[0]}
                    getSelection={(option: any) =>
                      handleInputChange("shift", option)
                    }
                  />
              </div>
              <div className="w-[25rem]">
                <SelectDuration
                  options={dynmaicDurationOption}
                  labelName="Duration"
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  isReset={false}
                  isDisabled={false}
                  handleChange={handleOptionChange}
                  defaultValue={selectedOption}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  onClose={handleClearAndFetch}
                />
              </div>
              <div className="h-10 w-[1px] bg-gray-300"></div>
              <IconTextButton
                label="Refresh"
                icon={<RefreshIcon />}
                btnSize="medium"
                handleClick={handleRefresh}
              />
            </div>
          </div>
          
          {/*Flex layout starts here */}
          <div
            className="flex flex-col lg:flex-row gap-6 w-full mt-3"
            ref={dashboardContainer}
          >
            <div className="flex items-center" style={{ height: "65vh" }}>
              {currentIndex > 1 && (
                <button
                  onClick={handlePrevPage}
                  className="bg-io-gray-e9 p-2 rounded-full z-10"
                >
                  <ChevronLeft className="w-10 h-10 md:w-12 md:h-12"/>
                </button>
              )}
            </div>
            
            <div className="w-full relative space-y-8">
              {/* Scrollable card block */}
              {currentIndex !== totalDashboardViews && (
                <div className="absolute inset-0 transition-opacity duration-700 ease-in-out opacity-100 z-10 overflow-y-auto">
                  {splitDashboard?.map((siteGroup, idx) => (
                    <div className="w-full" style={{ height: '35vh' }}>
                      <ScrollableSplitCardBlock
                        key={idx}
                        title={siteGroup.title}
                        site={siteGroup}
                        cardContainerRef={cardContainerRef}
                        scrollLeft={scrollLeft}
                        scrollRight={scrollRight}
                        showLeftButton={showLeftButton}
                        showRightButton={showRightButton}
                        expandedSite={expandedSite}
                        setExpandedSite={setExpandedSite}
                        cardHeight={cardSize.height}
                        cardWidth={cardSize.width}
                        isSplitScreen={true}
                        accuracyData={accuracyData}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Pie chart block */}
              {splitDashboard.length > 0 && currentIndex === totalDashboardViews && (
                <div className="absolute inset-0 transition-opacity duration-700 ease-in-out opacity-100 z-10 grid grid-cols-2 gap-4 overflow-y-auto content-start">
                  {splitDashboard.map((data, index) => {
                    const isLastOdd = splitDashboard.length % 2 !== 0 && index === splitDashboard.length - 1;
                    return (
                      <div key={index} className={isLastOdd ? "col-span-2" : ""} style={{ height: "65vh" }}>
                        <MultipleSplitRadialBars
                          title={data.title}
                          isSplitScreen={true}
                          cardsData={data.labels}
                          size={250}
                          outerRadius={"130%"}
                          cardHeight={cardSize.height * 2}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center" style={{ height: "65vh" }}>
              {splitDashboard.length > 0 && currentIndex < totalDashboardViews && (
                <button
                  onClick={handleNextPage}
                  className="bg-io-gray-e9 p-2 rounded-full z-10"
                >
                  <ChevronRight className="w-10 h-10 md:w-12 md:h-12"/>
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ReactDashboardUI;
